import apiClient from './AuthService';

// Interfaces para el reporte
export interface ProjectResponseReport {
    project_id: string;
    project_name: string;
    client_email: string;
    intervention_type: string;
    surface: string;
    created_at: string;
    groups: ReportGroup[];
    challenges: ChallengesSummary;
}

export interface ReportGroup {
    group_id: string;
    group_name: string;
    position: number;
    submitted: boolean;
    sections: ReportSection[];
}

export interface ReportSection {
    section_id: string;
    section_name: string;
    section_type: string;
    position: number;
    enabled: boolean;
    contents: ReportContent[];
}

export interface ReportContent {
    content_id: string;
    content_name: string;
    content_type: 'TEXT_SIMPLE' | 'TEXT_SELECTABLE' | 'QUESTION_SIMPLE' | 'QUESTION_MULTIPLE' | 'QUESTION_IMAGE' | 'CHALLENGE' | 'IMAGE_COMMENT';
    question: Record<string, unknown>;
    response: ReportResponse | null;
    is_completed: boolean;
    is_included: boolean;
    position: number;
    challenge_data?: ChallengeData[] | null;
}

// Union type for challenge data that can be embedded in content
export type ChallengeData = PersonalityTraitsChallenge | VirtualSpacesChallenge | LandscapeAndTexturesChallenge | HandDoesntLieChallenge | SpatialExperienceReportChallenge | SpatialExperienceRessoConfigReportChallenge | SpatialExperienceRessoReportChallenge;

export interface ReportResponse {
    response_id: string;
    respondent_id: string;
    respondent_email: string;
    response_content: string;
    response_type: string;
    created_at: string;
    updated_at: string;
}

export interface ChallengesSummary {
    personality_traits: PersonalityTraitsChallenge[];
    virtual_spaces: VirtualSpacesChallenge[];
    landscape_and_textures: LandscapeAndTexturesChallenge[];
    hand_doesnt_lie: HandDoesntLieChallenge[];
}

export interface PersonalityTraitsChallenge {
    personality_traits_challenge_id: string;
    project_id: string;
    user_id: string;
    member_id: string;
    member_name?: string;
    traits: string;
    together: boolean;
    created_at: string;
    updated_at: string;
}

export interface VirtualSpacesChallenge {
    virtual_spaces_challenge_id: string;
    project_id: string;
    user_id: string;
    member_id: string;
    image_url: string;
    comment: string;
    points: string;
    cozy: number;
    spacious: number;
    bright: number;
    created_at: string;
    updated_at: string;
}

export interface LandscapeAndTexturesChallenge {
    landscape_and_textures_challenge_id: string;
    project_id: string;
    user_id: string;
    member_id: string;
    room: string;
    activity1?: string;
    activity2?: string;
    activity3?: string;
    image_activity1?: string;
    image_activity2?: string;
    image_activity3?: string;
    answer_activity1?: string;
    answer_activity2?: string;
    answer_activity3?: string;
    texture_activity1?: string;
    texture_activity2?: string;
    texture_activity3?: string;
    coords_activity1?: string;
    coords_activity2?: string;
    coords_activity3?: string;
    texture_answer_activity1?: string;
    texture_answer_activity2?: string;
    texture_answer_activity3?: string;
    created_at: string;
    updated_at: string;
}

export interface HandDoesntLieChallenge {
    hand_doesnt_lie_challenge_id: string;
    project_id: string;
    user_id: string;
    member_id: string;
    member_name?: string;
    image_url_1?: string;
    image_url_2?: string;
    image_url_3?: string;
    image_url_4?: string;
    image_url_5?: string;
    image_url_6?: string;
    image_url_7?: string;
    image_url_8?: string;
    image_url_9?: string;
    image_url_10?: string;
    created_at: string;
    updated_at: string;
}

export interface SpatialExperienceReportChallenge {
    spatial_experience_challenge_id: string;
    project_id: string;
    user_id?: string;
    member_id: string;
    member_name?: string;
    step: 'FIRST' | 'SECOND';
    scene_number: number;
    scene_title?: string;
    scene_text?: string;
    scene_comments?: string;
    positions?: string;
    day_type: 'WORKING' | 'HOLIDAY';
    created_at: string;
    updated_at: string;
}

export interface SpatialExperienceRessoConfigReportChallenge {
    spatial_experience_resso_config_challenge_id: string;
    project_id: string;
    user_id?: string;
    label: string;
    coords: string;
    created_at: string;
    updated_at: string;
}

export interface SpatialExperienceRessoReportChallenge {
    spatial_experience_resso_challenge_id: string;
    project_id: string;
    user_id?: string;
    member_id: string;
    member_name?: string;
    step: 'FIRST' | 'SECOND';
    scene_number: number;
    scene_title?: string;
    scene_text?: string;
    scene_comments?: string;
    day_type: 'WORKING' | 'HOLIDAY';
    floor_type: 'FIRST_FLOOR' | 'SECOND_FLOOR';
    positions?: string;
    enclosures?: string;
    created_at: string;
    updated_at: string;
}

// API Functions

/**
 * Get the project response report as JSON
 */
export const getProjectReport = async (projectId: string): Promise<ProjectResponseReport> => {
    const response = await apiClient.get(`/quiz/projects/${projectId}/report`);
    return response.data.data;
};

/**
 * Get project report as HTML
 */
export const getProjectReportHtml = async (projectId: string): Promise<string> => {
    const response = await apiClient.get(`/quiz/projects/${projectId}/report/html`, {
        responseType: 'text'
    });
    return response.data;
};

/**
 * Download project report as PDF
 */
export const downloadProjectReportPdf = async (projectId: string, projectName?: string): Promise<void> => {
    const response = await apiClient.get(`/quiz/projects/${projectId}/report/pdf`, {
        responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Use project name for filename if provided, otherwise use project ID
    const filename = projectName
        ? `reporte-${projectName.toLowerCase().replace(/\s+/g, '-')}.pdf`
        : `reporte-proyecto-${projectId}.pdf`;

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

export default {
    getProjectReport,
    getProjectReportHtml,
    downloadProjectReportPdf
};
